import React from 'react';
import { motion } from 'framer-motion';

export default function TermsOfService() {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="container py-16 space-y-12"
    >
      <section className="section--tint-light rounded-lg p-12 space-y-6">
        <h1 className="text-4xl font-bold text-center">Terms of Service</h1>
        <p className="text-center text-gray-600 italic">Last updated: 17 May 2026</p>

        <div className="bg-white rounded-md border border-gray-200 p-6 space-y-3">
          <h2 className="text-2xl font-semibold">What changed in this update</h2>
          <p>
            We have added clearer terms for uploaded images and videos, how they are checked,
            when they may be reviewed, and when they may be deleted. In plain language:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Posts, replies, comments, messages, uploads, files, and links are your responsibility.</li>
            <li>Images and videos may be quarantined, scanned, optimized, thumbnailed, and access-controlled before use.</li>
            <li>Flagged uploads may be blocked, held for Member Nest support review, approved as false positives, or deleted.</li>
            <li>Unused, incomplete, discarded, moderated, or no-longer-visible uploaded media may be deleted.</li>
            <li>Moderation cases, audit records, and limited evidence may be retained where needed to protect users and clubs.</li>
          </ul>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-semibold mb-2">1. Who These Terms Cover</h2>
            <p>
              These terms apply to everyone using Member Nest, including club administrators,
              organisers, members, parents, guardians, and users managing accounts for someone else.
            </p>
            <p className="mt-2">
              You must accept these terms before creating an account or using Member Nest.
              If you use Member Nest for a club, you must also have authority to act for that club.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">2. What Member Nest Provides</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Create and manage clubs, teams, memberships, families, events, and payments</li>
              <li>Join clubs, respond to events, manage membership details, and use club communications</li>
              <li>Use message boards, event comments, inbox messages, emails, and notifications</li>
              <li>Upload or create club content, including logos, documents, images, videos, events, posts, replies, and comments</li>
              <li>Use payment features provided through Stripe where a club has enabled them</li>
              <li>Let a club manage membership payment outside Member Nest where that club selects a club-managed mode</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">3. Accounts and Club Administration</h2>
            <p>
              You are responsible for keeping your login secure and for activity carried out through
              your account. Club administrators are responsible for configuring their club correctly,
              inviting appropriate users, managing member access, setting prices, handling refunds, and
              making sure the club has a lawful basis for the information it asks members to provide.
            </p>
            <p className="mt-2">
              We or club administrators may remove users, disable features, revoke board access, or
              restrict posting where needed to protect members, the club, or Member Nest.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">4. Children and Managed Accounts</h2>
            <p>
              Member Nest supports clubs that include children and family groups. Parents, guardians,
              and club administrators must only create, manage, or invite child users where they have
              appropriate authority to do so. Children should use Member Nest with parent, guardian, or
              club support suitable for their age and club setting.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">4A. Migrating Existing Members</h2>
            <p>
              Clubs may import existing member records only where they are authorised to do so and
              must map the information to appropriate Member Nest fields and current membership
              options. An import creates an unclaimed club record, not a login account. Members must
              sign in or register with the invited email address and review their imported details.
            </p>
            <p className="mt-2">
              Clubs are responsible for the accuracy of imported paid-through dates, family groups,
              guardian authority, subscription mappings, and other migrated information. Parents or
              guardians must confirm child records before managed child accounts are created.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">5. User Content and Message Boards</h2>
            <p>
              You are responsible for the posts, replies, event comments, messages, uploads, files, links, and
              other content you submit. By submitting content, you give Member Nest and the relevant
              club permission to host, store, process, display, notify users about, moderate, and copy
              that content as needed to provide the service.
            </p>
            <p className="mt-2">
              Board content is visible according to the club's board, tag, event, and membership
              settings. It is not a private storage area. Do not post confidential information,
              payment card details, medical details, safeguarding information, or personal contact
              details unless the club has clearly asked for them in an appropriate place.
            </p>
            <p className="mt-2">
              Where uploaded media is enabled, you may be able to upload images or videos such as
              JPEG, PNG, WebP, MP4, MOV, or WebM files. Member Nest may store uploads, scan them,
              optimize or transcode them, create thumbnails, control access to them, display them in
              the feature where they were uploaded, and make them available to Member Nest support for
              review where they are flagged or reported.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">6. Acceptable Use</h2>
            <p>You must not use Member Nest to post, send, upload, request, or link to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Illegal content or instructions for illegal activity</li>
              <li>Harassment, threats, abuse, hate, bullying, or discriminatory content</li>
              <li>Sexual, exploitative, grooming, or otherwise inappropriate content, especially involving children</li>
              <li>Spam, scams, phishing, malware, or attempts to obtain login, payment, or identity details</li>
              <li>Impersonation or misleading claims about your identity, role, club, or authority</li>
              <li>Content that infringes someone else's rights, including privacy or intellectual property rights</li>
              <li>Confidential information, payment card details, medical details, safeguarding information, or child-sensitive content unless the club or platform has clearly requested it in the correct place</li>
              <li>Attempts to move child or safeguarding-sensitive conversations to private off-platform channels</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">7. Reporting, Moderation, and Reviews</h2>
            <p>
              Member Nest may use automated checks, member reports, club administrator review, and
              Member Nest support review to identify content that may breach these terms or create a
              safeguarding, security, legal, or community risk. We may warn users, hide content,
              remove content from club discussion, retain evidence for review, lock topics, mute users,
              revoke board privileges, suspend accounts, contact club administrators, or take other
              proportionate action.
            </p>
            <p className="mt-2">
              Uploaded media is checked before it becomes available. Images may be scanned directly.
              Videos may be checked using sampled frames. Approved uploads are moved from quarantine
              to approved storage and are only served through access checks or time-limited signed
              access. Flagged uploads may be blocked, held for support review, approved if they were
              flagged incorrectly, or deleted.
            </p>
            <p className="mt-2">
              Unused or incomplete uploads may be cleaned up automatically. Uploaded media may also be
              deleted when you discard it, when content is removed through moderation, when board
              content is deleted, or when direct-message media is no longer visible to any
              participant. Moderation cases, audit records, and limited evidence may remain where
              needed for safety, dispute handling, legal compliance, or platform protection.
            </p>
            <p className="mt-2">
              We do not promise to review every post before it appears. If you see content that may be
              illegal, harmful, abusive, or unsafe, use the in-product reporting tools where available
              or email <strong>privacy@member-nest.com</strong>. If there is an immediate risk of harm,
              contact the appropriate emergency, safeguarding, or law enforcement service first.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">8. Payments and Pricing</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Card and subscription payments are processed by Stripe</li>
              <li>Member Nest does not store your full card details</li>
              <li>Each club sets its own membership prices, event prices, refunds, and payment rules</li>
              <li>Clubs using club-managed membership payments authorise Member Nest to charge their saved billing card annually for each qualifying member recorded during the billing period, or to close and charge the current period before an earlier club deletion completes</li>
              <li>A member qualifies for that annual count after remaining in the club continuously for at least 24 hours; the club can review the supporting audit before collection</li>
              <li>Paid events, courses, bolt-ons, moorings, and other in-product payment features require the club to maintain an operational connected Stripe account</li>
              <li>Member Nest may manually restrict a club with unresolved platform charges; data is preserved and authorised club administrators retain access to billing resolution</li>
              <li>Contact your club first for billing, membership, event, or refund questions</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">9. Service Availability and Changes</h2>
            <p>
              We aim to provide a reliable service, but Member Nest may be unavailable for maintenance,
              fixes, security work, provider outages, or upgrades. We may add, change, limit, or remove
              features where needed to improve or protect the service.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">10. Liability</h2>
            <p>
              Member Nest provides club management software. Clubs remain responsible for their own
              decisions, membership rules, safeguarding processes, financial choices, communications,
              and disputes with members. To the fullest extent allowed by law, we are not responsible
              for indirect losses, loss of profits, loss of data caused by your own actions, or disputes
              between users or clubs.
            </p>
            <p className="mt-2">
              Nothing in these terms excludes liability that cannot lawfully be excluded, including
              liability for fraud or for death or personal injury caused by negligence.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">11. Suspension and Termination</h2>
            <p>
              We may suspend, restrict, or terminate access if these terms are broken, if the platform
              is misused, if we need to protect users or clubs, or if we are required to do so by law.
              Clubs may also remove members from their own club spaces.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">12. Governing Law</h2>
            <p>
              These terms are governed by the laws of England and Wales. Disputes will be handled in
              the courts of England and Wales unless consumer protection law gives you the right to use
              another court.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">13. Changes to These Terms</h2>
            <p>
              We may update these terms from time to time. If a change is significant, we will take
              reasonable steps to tell users, such as by email or in-app notice. Continued use of
              Member Nest after the updated terms take effect means you accept them.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-2">Questions?</h2>
            <p>Email <strong>privacy@member-nest.com</strong> if you have any questions about these terms.</p>
          </div>
        </div>
      </section>
    </motion.main>
  );
}
